import React, { useState } from 'react';

import { link } from '@aline/routes';
import { gql } from '@apollo/client';
import { Divider, IconButton, Skeleton, Typography } from '@mui/material';
import { Helmet } from 'react-helmet';
import { Link, useNavigate } from 'react-router-dom';
import { AlineInput } from 'src/AlineInput';
import { AlineButton, ConfirmationDialog, ToggleSwitch } from 'src/components';
import { groupAdminRowHeightPx, GroupUsersList } from 'src/components/admin';
import { useAccessGuard, useTierPageGuard } from 'src/components/auth';
import { BackArrowIcon } from 'src/components/icons/BackArrowIcon';
import { useSelectCurrentUser } from 'src/gql';
import { Apollo } from 'src/gql/generated';
import { useGroupParams } from 'src/helpers';
import { useDeleteGroup, useGetGroup } from 'src/hooks';
import { makeStyles } from 'tss-react/mui';
import { useDebouncedCallback } from 'use-debounce';

const useStyles = makeStyles()(theme => ({
  backButton: {
    marginRight: theme.spacing(1),
  },
  divider: {
    marginBottom: theme.spacing(2),
  },
  goBackButton: {
    marginRight: theme.spacing(2),
  },
  groupActions: {
    alignItems: 'center',
    display: 'flex',
    padding: theme.spacing(2, 0),
  },
  groupName: {
    marginBottom: theme.spacing(3),
  },
  innerContainer: {
    display: 'flex',
    flexDirection: 'column',
    maxWidth: 800,
    width: '70%',
  },
  inputLabel: {
    ...theme.appTypography.fieldLabel,
    marginBottom: theme.spacing(2),
  },
  label: {
    ...theme.appTypography.fieldLabel,
    fontSize: 14,
    marginBottom: theme.spacing(2),
  },
  loadingList: {
    marginBottom: theme.spacing(2),
    maxHeight: groupAdminRowHeightPx * 6,
    minHeight: groupAdminRowHeightPx * 6,
  },
  permissionText: {
    marginBottom: theme.spacing(1),
  },
  scrollContainer: {
    display: 'flex',
    flexGrow: 1,
    justifyContent: 'center',
    overflowY: 'auto',
  },
  titleContainer: {
    alignItems: 'center',
    display: 'flex',
    marginBottom: theme.spacing(4),
  },
}));

export function Group(): JSX.Element {
  const { classes } = useStyles();
  const navigate = useNavigate();
  const { groupId } = useGroupParams();
  const { deleteGroup } = useDeleteGroup();
  const currentUser = useSelectCurrentUser();
  const { group } = useGetGroup(groupId, {
    fetchPolicy: 'cache-and-network',
    nextFetchPolicy: 'cache-first',
  });
  const [deleteDialogOpen, setDeleteDialogOpen] = useState<boolean>(false);
  const isCurrentUserAdmin = currentUser?.role === Apollo.OrgRole.Admin;
  const groupNameText = `"${group?.name || 'Untitled'}" Group`;
  const handleOpenDeleteDialog = useAccessGuard({
    callback: () => setDeleteDialogOpen(true),
    minRole: Apollo.OrgRole.Admin,
  });

  useTierPageGuard(Apollo.SubscriptionTier.Enterprise);

  async function handleDeleteGroup(): Promise<void> {
    const { data } = await deleteGroup({ variables: { id: groupId } });
    if (data) navigate(link.AdminGroupIndex());
  }

  if (!group)
    return (
      <>
        <div className={classes.titleContainer}>
          <Skeleton className={classes.backButton} height={32} variant="circular" width={32} />
          <Typography variant="h2">
            <Skeleton variant="text" width={100} />
          </Typography>
        </div>
        <div className={classes.scrollContainer}>
          <div className={classes.innerContainer}>
            <Typography className={classes.label}>
              <Skeleton variant="text" width={150} />
            </Typography>
            <Divider className={classes.divider} variant="fullWidth" />
            <Typography className={classes.inputLabel}>
              <Skeleton variant="text" width={150} />
            </Typography>
            <Skeleton
              className={classes.groupName}
              height={32}
              variant="rectangular"
              width="100%"
            />
            <Typography className={classes.label}>
              <Skeleton variant="text" width={150} />
            </Typography>
            <Divider className={classes.divider} variant="fullWidth" />
            <Typography className={classes.permissionText}>
              <Skeleton variant="text" width={400} />
            </Typography>
            <Skeleton className={classes.loadingList} variant="rectangular" width="100%" />
          </div>
        </div>
      </>
    );

  return (
    <>
      <Helmet>
        <title>{`${groupNameText} - Admin`}</title>
      </Helmet>
      <div className={classes.titleContainer}>
        <IconButton
          className={classes.backButton}
          component={Link}
          size="small"
          to={link.AdminGroupIndex()}
        >
          <BackArrowIcon />
        </IconButton>
        <Typography variant="h2">{groupNameText}</Typography>
      </div>
      <div className={classes.scrollContainer}>
        <div className={classes.innerContainer}>
          <Typography className={classes.label}>Group Details</Typography>
          <Divider className={classes.divider} variant="fullWidth" />
          <Typography className={classes.inputLabel}>Group Name</Typography>
          <GroupNameInput
            className={classes.groupName}
            disabled={!isCurrentUserAdmin}
            group={group}
          />
          <Typography className={classes.inputLabel}>Group Permissions</Typography>
          <ToggleAllDocAndWorkflows group={group} />
          <Typography className={classes.label}>Users</Typography>
          <Divider className={classes.divider} variant="fullWidth" />
          <GroupUsersList groupId={groupId} />
          <div className={classes.groupActions}>
            <AlineButton
              className={classes.goBackButton}
              color="primary"
              component={Link}
              to={link.AdminGroupIndex()}
              variant="outlined"
            >
              Go Back
            </AlineButton>
            <AlineButton color="primary" variant="contained" onClick={handleOpenDeleteDialog}>
              Delete Group
            </AlineButton>
          </div>
        </div>
      </div>
      <ConfirmationDialog
        message="Are you sure you want to delete this group? This action cannot be undone."
        open={deleteDialogOpen}
        title="Delete group"
        onClickPrimary={handleDeleteGroup}
        onClose={() => setDeleteDialogOpen(false)}
      />
    </>
  );
}

interface GroupNameProps extends React.HTMLAttributes<HTMLElement> {
  group: Pick<Apollo.Group, 'id' | 'name' | '__typename'>;
  disabled?: boolean;
}

function GroupNameInput({ className, disabled, group }: GroupNameProps): JSX.Element {
  const [name, setName] = useState<string>(group.name);
  const [updateGroup] = Apollo.useUpdateGroupMutation();
  const debouncedUpdateGroup = useDebouncedCallback(updateGroup, 500);

  return (
    <AlineInput
      fullWidth
      className={className}
      disabled={disabled}
      placeholder="Enter group name..."
      value={name}
      onChange={async e => {
        setName(e.target.value);
        await debouncedUpdateGroup({
          variables: { input: { id: group.id, props: { name: e.target.value } } },
        });
      }}
    />
  );
}

void gql`
  mutation UpdateGroupGlobalAccess($input: UpdateGroupInput!) {
    updateGroup(input: $input) {
      __typename
      id
      canAccessAllDocAndWorkflows
    }
  }
`;

const useToggleAllDocAndWorkflowsStyles = makeStyles()(theme => ({
  row: {
    alignItems: 'center',
    display: 'flex',
    gap: theme.spacing(1),
    marginBottom: theme.spacing(2),
  },
}));

function ToggleAllDocAndWorkflows({
  group,
}: {
  group: Pick<Apollo.Group, 'id' | 'name' | 'canAccessAllDocAndWorkflows' | '__typename'>;
}): JSX.Element {
  const { classes } = useToggleAllDocAndWorkflowsStyles();
  const [updateGroup] = Apollo.useUpdateGroupGlobalAccessMutation();
  const msg = group.canAccessAllDocAndWorkflows
    ? `"${group.name}" has Viewer access to all documents and workflows`
    : `"${group.name}" has access to documents and workflows shared directly or through labels`;

  return (
    <div className={classes.row}>
      <ToggleSwitch
        toggle={async () => {
          await updateGroup({
            optimisticResponse: {
              __typename: 'Mutation',
              updateGroup: {
                __typename: 'Group',
                canAccessAllDocAndWorkflows: !group.canAccessAllDocAndWorkflows,
                id: group.id,
              },
            },
            variables: {
              input: {
                id: group.id,
                props: { canAccessAllDocAndWorkflows: !group.canAccessAllDocAndWorkflows },
              },
            },
          });
        }}
        toggled={group.canAccessAllDocAndWorkflows}
      />
      <Typography>{msg}</Typography>
    </div>
  );
}
